import React, { useMemo, useState } from 'react';
import { useTheme } from '../theme';

// Base64 packs 3 input bytes into 4 output characters, padded up to a multiple of 4.
function encodedSizeForBytes(byteLength: number): number {
  return byteLength === 0 ? 0 : Math.ceil(byteLength / 3) * 4;
}

export default function Base64SizeCalculator() {
  const { themeClasses } = useTheme();
  const [text, setText] = useState('');
  const [byteInput, setByteInput] = useState('');

  const textBytes = useMemo(() => new TextEncoder().encode(text).length, [text]);
  const textEncodedSize = encodedSizeForBytes(textBytes);

  const rawBytes = Math.max(0, parseInt(byteInput, 10) || 0);
  const rawEncodedSize = encodedSizeForBytes(rawBytes);

  const overheadPct = (original: number, encoded: number) =>
    original === 0 ? 0 : Math.round(((encoded - original) / original) * 1000) / 10;

  return (
    <div className={`${themeClasses.cardBg} rounded-3xl border p-5 sm:p-8 space-y-8`}>
      <div>
        <label className={`block text-lg font-semibold mb-3 ${themeClasses.textPrimary}`}>
          Paste text to measure
        </label>
        <textarea
          value={text}
          onChange={(e) => setText(e.target.value)}
          placeholder="Paste or type text here..."
          aria-label="Text to measure"
          className={`w-full h-32 p-4 ${themeClasses.inputBg} border rounded-2xl resize-none`}
        />
        <div className={`mt-4 grid grid-cols-3 gap-3 text-center`}>
          <div className={`${themeClasses.infoCard} rounded-xl p-3 border`}>
            <div className={`text-2xl font-bold ${themeClasses.textPrimary}`}>{textBytes.toLocaleString()}</div>
            <div className={`text-xs ${themeClasses.textMuted}`}>original bytes</div>
          </div>
          <div className={`${themeClasses.infoCard} rounded-xl p-3 border`}>
            <div className={`text-2xl font-bold ${themeClasses.accentText}`}>{textEncodedSize.toLocaleString()}</div>
            <div className={`text-xs ${themeClasses.textMuted}`}>Base64 characters</div>
          </div>
          <div className={`${themeClasses.infoCard} rounded-xl p-3 border`}>
            <div className={`text-2xl font-bold ${themeClasses.textPrimary}`}>+{overheadPct(textBytes, textEncodedSize)}%</div>
            <div className={`text-xs ${themeClasses.textMuted}`}>size overhead</div>
          </div>
        </div>
      </div>

      <div>
        <label className={`block text-lg font-semibold mb-3 ${themeClasses.textPrimary}`}>
          Or enter a byte count (e.g. file size)
        </label>
        <input
          type="number"
          min={0}
          value={byteInput}
          onChange={(e) => setByteInput(e.target.value)}
          placeholder="e.g. 1048576 for 1 MB"
          aria-label="Byte count"
          className={`w-full p-4 ${themeClasses.inputBg} border rounded-2xl`}
        />
        {byteInput && (
          <p className={`mt-3 ${themeClasses.textSecondary}`}>
            {rawBytes.toLocaleString()} bytes encodes to{' '}
            <span className={`font-semibold ${themeClasses.accentText}`}>{rawEncodedSize.toLocaleString()} characters</span>{' '}
            — about {overheadPct(rawBytes, rawEncodedSize)}% larger.
          </p>
        )}
      </div>
    </div>
  );
}
